'use client';
import useStripeDonateDialogStore from '@/components/store/stripeDonateDialogSlice';
import React, { useState, useEffect } from 'react';
import GiftOfGivingDonationForm from './GiftOfGivingDonationForm';
import StripeDonationForm from './StripeDonationForm';
import Dialog from '@ui/Dialog';
import {
  makeGiftOfGivingDonation,
  raiseDonationIntent,
  raiseDonationIntentPublic,
  raiseRecurringDonationIntent,
  raiseStripeDonationIntent,
  raiseStripeDonationIntentPublic,
} from '@/components/store/stripeDonate.actions';
import NonProfitProfileBar from './NonProfitProfileBar';
import useUserProfile from '@/hooks/useUserProfile';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import CircularLoading from '@/components/CircularLoader/CircularLoader';
import Button from '@ui/Button';
import { cx } from 'cva';
import ActivitySuccessDialog from '../ActivitySuccessDialog';
import { hasAppAccess } from '@/stores/userActions';
import { AppPreferenceItem } from '@/utils/appPreference';
import { differenceInDays } from 'date-fns';
import ExternalDonationForm from './ExternalDonationForm';
import { fetchViaProxy } from '@/actions/proxy-fetch';
import {
  fetchCommunityById,
  fetchNonProfitById,
  fetchPostById,
} from '@/actions/server.actions';
import useStripeContributionDialogStore from '../store/stripeContributionDialogSlice';
import ContributionSuccessDialog from '../ActivitySuccessDialog/ContributionSuccessDialog';
import StripeContributionForm from './StripeContributionForm';
import ExternalContributionForm from './ExternalContributionForm';
import {
  raiseStripeContributionIntent,
  raiseStripeContributionIntentPublic,
} from '../store/stripeContribution.actions';
import FundraisingOrgProfileBar from './FundraisingOrgProfileBar';
import { isContributeDisabled } from '@/utils/donate.helper';

const tabs = [{ name: 'Make a Contribution' }];

function ContributionDialog(props) {
  const isOpen = useStripeContributionDialogStore((state) => state.open);
  const amount = useStripeContributionDialogStore((state) => state.amount);
  const post = useStripeContributionDialogStore((state) => state.post);
  const setIsOpen = useStripeContributionDialogStore((state) => state.setOpen);
  const fundraisingOrg = useStripeContributionDialogStore(
    (state) => state.fundraisingOrg,
  );

  const user = useUserProfile();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [showLoader, setShowLoader] = useState(false);
  const [showSuccess, setShowSuccess] = useState(null);
  const [selectedFundraisingOrg, setSelectedFundraisingOrg] = useState(null);
  const [externalContributionIntent, setExternalContributionIntent] =
    useState(null);

  const [selectedTab, setSelectedTab] = useState(tabs[0]);

  const [loggedOutUser, setLoggedOutUser] = useState(null);

  const hasContributionUrl = !!post?.sourcePostData?.contributionUrl;
  const hasStripeId = !!fundraisingOrg?.stripeId;

  const canContribute = !isContributeDisabled(fundraisingOrg, post);

  function closeModal() {
    setIsOpen(false);
    // setSelectedTab(tabs[0]);
    setShowSuccess(false);
    if (externalContributionIntent) {
      setExternalContributionIntent(null);
      router.replace(pathname);
    }
  }

  const isLoggedIn = () => {
    return user?.id ? user.id > 0 : false;
  };

  async function onSubmit(data, isRecurring = false) {
    let payload = {
      ...data,
      tip: (data.amount * data.tip[0]) / 1000,
    };

    if (user?.id !== -1) {
      payload = {
        ...payload,
        firstName: user?.firstName || user.first_name,
        lastName: user?.lastName || user.last_name,
        email: user?.email,
      };
    }

    setShowLoader(true);
    if (isRecurring) {
      const res = await raiseStripeContributionIntent(
        fundraisingOrg,
        payload,
        post,
        isRecurring,
      );
      console.log('raiseStripeContributionIntent Recurring', res);
      if (res) {
        window.open(res.url, '_self');
      }
    } else {
      if (isLoggedIn()) {
        const res = await raiseStripeContributionIntent(
          fundraisingOrg,
          payload,
          post,
        );
        console.log('raiseStripeDonationIntent', res);
        if (res) {
          window.open(res.url, '_self');
        }
      } else {
        const res = await raiseStripeContributionIntentPublic(
          fundraisingOrg,
          payload,
          post,
        );
        console.log('raiseStripeContributiontentPublic', res);
        if (res) {
          window.open(res.url, '_self');
        }
      }
    }
    setShowLoader(false);
  }

  async function handleFetchExternalContributionPledgeIntent(
    intentId,
    pendingTask = true,
  ) {
    let res = null;

    if (isLoggedIn()) {
      const { ok, data } = await fetchViaProxy(
        `/proxy/v2/protected/users/me/external/contributions/intent/${intentId}`,
      );

      if (!ok) {
        console.log('intent not found');
        return;
      }

      res = data?.data;

      if (res?.userId !== user?.id) {
        router.replace('/api/auth/logout');
      }
    } else {
      const responseData = await fetch(
        `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/contributions/external/intent/${intentId}`,
      );

      if (!responseData.ok) {
        console.log('Public intent not found');
        return;
      }
      const { data } = await responseData.json();

      res = data;
    }

    setExternalContributionIntent(res);

    if (!pendingTask) {
      return;
    }

    const fundraisingOrgId = res?.fundraisingOrgId;
    const postId = res?.postId;
    const amount = res?.amount;

    console.log('postId', postId);

    // create fetch fundraising org Id
    const [fundraisingOrgById, postById] = await Promise.all([
      fetchCommunityById(fundraisingOrgId),
      fetchPostById(postId),
    ]);

    setSelectedFundraisingOrg(fundraisingOrgById);

    console.log('fundraisingOrgById', fundraisingOrgById);
    console.log('postById', postById);

    setIsOpen(true, {
      fundraisingOrg: fundraisingOrgById,
      amount: amount,
      post: postById,
    });
  }

  useEffect(() => {
    if (isLoggedIn()) {
      const params = new URLSearchParams(searchParams);

      if (params.get('contributionIntentId')) {
        const intentId = params.get('contributionIntentId');
        handleFetchExternalContributionPledgeIntent(intentId);
      }
    }
  }, [searchParams]);

  // useEffect(() => {
  //   if (externalContributionIntent) {
  //     setSelectedTab(tabs[1]);
  //   }
  // }, [selectedFundraisingOrg, externalContributionIntent]);

  const renderTabs = () => {
    if (externalContributionIntent) {
      return null;
    }
    return (
      <div className="mt-1 flex gap-4">
        {canContribute && (
          <Button
            // onClick={() => setSelectedTab(tabs[1])}
            variant={'outlined'}
            color="transparent"
            className={cx(
              'border border-primary text-15 font-medium tracking-regular text-primary',
            )}>
            Make a Contribution
          </Button>
        )}
      </div>
    );
  };

  const renderDonationForm = () => {
    if (externalContributionIntent) {
      return (
        <ExternalContributionForm
          user={user}
          post={post}
          amount={amount || ''}
          fundraisingOrg={fundraisingOrg}
          isLoggedIn={isLoggedIn}
          handleClose={closeModal}
          setShowSuccess={setShowSuccess}
          setLoggedOutUser={setLoggedOutUser}
          contributionIntent={externalContributionIntent}
          handleFetchContributionIntent={
            handleFetchExternalContributionPledgeIntent
          }
        />
      );
    }

    return hasStripeId ? (
      <StripeContributionForm
        onSubmit={onSubmit}
        amount={amount || ''}
        fundraisingOrg={fundraisingOrg}
        isLoggedIn={isLoggedIn}
      />
    ) : (
      <ExternalContributionForm
        user={user}
        post={post}
        amount={amount || ''}
        fundraisingOrg={fundraisingOrg}
        isLoggedIn={isLoggedIn}
        handleClose={closeModal}
        setShowSuccess={setShowSuccess}
        setLoggedOutUser={setLoggedOutUser}
        contributionIntent={externalContributionIntent}
        handleFetchContributionIntent={
          handleFetchExternalContributionPledgeIntent
        }
      />
    );
  };

  if (!selectedFundraisingOrg && !isOpen) {
    return null;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeModal}
      maxWidth="lg"
      contentClass="!p-0"
      titleClass="px-6 pt-6 lg:px-8 lg:pt-8">
      {showSuccess ? (
        <ContributionSuccessDialog
          post={post}
          onClose={closeModal}
          user={isLoggedIn() ? user : loggedOutUser}
        />
      ) : (
        <div className="mb-10 mt-4 flex flex-1 flex-col gap-6">
          <FundraisingOrgProfileBar
            fundraisingOrg={fundraisingOrg}
            post={post}
          />
          <div className="flex flex-col">
            <div className="flex w-full flex-col gap-4 px-6 pt-2 lg:px-8 ">
              {renderTabs()}
              {renderDonationForm()}
            </div>
          </div>
          <CircularLoading loaderState={showLoader} />
        </div>
      )}
    </Dialog>
  );
}

export default ContributionDialog;
